import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import * as THREE from 'three';
import { eventBus } from './EventBus.js';

// Custom shader for fully green smoke detectors
const detectorVertexShader = `
    varying vec3 vNormal;
    varying vec3 vPosition;
    
    void main() {
        vNormal = normalize(normalMatrix * normal);
        vPosition = (modelViewMatrix * vec4(position, 1.0)).xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

const detectorFragmentShader = `
    uniform vec3 baseColor;
    uniform vec3 emissiveColor;
    uniform float emissiveIntensity;
    uniform float time;
    
    varying vec3 vNormal;
    varying vec3 vPosition;
    
    void main() {
        vec3 viewDir = normalize(-vPosition);
        vec3 normal = normalize(vNormal);
        
        // Rim lighting effect (glows more on edges)
        float rim = 1.0 - abs(dot(normal, viewDir));
        rim = pow(rim, 2.0);
        
        // Pulse effect
        float pulse = sin(time * 2.0) * 0.1 + 0.9;
        
        // Combine base color with emissive glow
        vec3 finalColor = baseColor + (emissiveColor * emissiveIntensity * pulse);
        
        // Add rim glow
        finalColor += emissiveColor * rim * emissiveIntensity * 0.5;
        
        gl_FragColor = vec4(finalColor, 1.0);
    }
`;

// Create the custom shader material
function createDetectorMaterial(state = 'online') {
    const colors = {
        online: { base: new THREE.Color(0x00aa00), emissive: new THREE.Color(0x00ff00) },
        offline: { base: new THREE.Color(0x888888), emissive: new THREE.Color(0x444444) },
        alarm: { base: new THREE.Color(0xaa0000), emissive: new THREE.Color(0xff0000) },
        test: { base: new THREE.Color(0xb45309), emissive: new THREE.Color(0xf59e0b) }
    };
    
    const colorSet = colors[state] || colors.online;
    
    return new THREE.ShaderMaterial({
        uniforms: {
            baseColor: { value: colorSet.base },
            emissiveColor: { value: colorSet.emissive },
            emissiveIntensity: { value: state === 'offline' ? 0.2 : 1.2 },
            time: { value: 0 }
        },
        vertexShader: detectorVertexShader,
        fragmentShader: detectorFragmentShader,
        side: THREE.DoubleSide
    });
}

export class DeviceManager {
    constructor(scene) {
        this.scene = scene;
        this.devices = new Map(); // Store active devices
        this.prototypes = {};     // Store loaded GLB prototypes
        this.loader = new GLTFLoader();

        eventBus.on('device:toggle', (id) => this.toggleDevice(id));
        eventBus.on('device:toggleMute', (id) => this.toggleMute(id));
        eventBus.on('device:test', (id) => this.testDevice(id));
        eventBus.on('fire:simulate', () => this.simulateFireAtSlot('slot_003'));
        eventBus.on('fire:reset', () => this.resetFire());

        
        // Fire simulation configuration
        this.fireConfig = {
            maxPropagation: 7,      // Max detectors to trigger
            baseDelay: 800,          // ms between alarms (was 2000)
            maxDelay: 5000,          // Cap total delay at 5 seconds
            useDistanceBasedDelay: true  // Closer = faster
        };
    }

    // Load detector_A.glb and detector_B.glb
    async loadPrototypes() {
        const loadGLB = (path) => new Promise((resolve) => {
            this.loader.load(path, (gltf) => resolve(gltf.scene));
        });

        this.prototypes.standard = await loadGLB('./detector_A.glb');
        this.prototypes.smart = await loadGLB('./detector_B.glb');
        
        console.log('Prototypes loaded:', Object.keys(this.prototypes));
    }

    // Load detectors.json
    async loadManifest(path) {
        const response = await fetch(path);
        return await response.json();
    }

    // Create one device instance
    createDevice(config, position) {
        const prototype = this.prototypes[config.type];
        if (!prototype) return;

        const device = prototype.clone();
        device.position.copy(position);

        device.userData = {
            id: config.id,
            type: config.type,
            state: config.state,
            slotId: config.slotId,
            isDevice: true
        };

        // Replace with custom fully green shader material
        const customMaterial = createDetectorMaterial(config.state);
        device.traverse((child) => {
            if (child.isMesh) {
                child.material = customMaterial;
            }
        });
        
        // Store reference to material for updates
        device.userData.customMaterial = customMaterial;

        // Create signal ring system
        const rings = this.createSignalRings(config.id);
        device.add(rings); // Attach rings to device so they move together

        this.scene.add(device);

        this.devices.set(config.id, {
            mesh: device,
            rings: rings,
            ringData: {
                active: false,
                speed: 2.0, // Ring expansion speed
                color: new THREE.Color(0x00ff00), // Default green
                lastSpawn: 0,
                spawnInterval: 0.5 // New ring every 0.5 seconds when active
            },
            ...config
        });
    }

    createSignalRings(deviceId) {
        const group = new THREE.Group();
    const ringCount = 3;
    
        for (let i = 0; i < ringCount; i++) {
            const geometry = new THREE.RingGeometry(0.3, 0.35, 32);
            const material = new THREE.MeshBasicMaterial({
                color: 0x00ff00,
                transparent: true,
                opacity: 0,
                side: THREE.DoubleSide,
                blending: THREE.AdditiveBlending,
                depthWrite: false
            });
            
            const ring = new THREE.Mesh(geometry, material);
            ring.rotation.x = -Math.PI / 2; // Lay flat on ceiling
            ring.userData = {
                age: 0,
                maxAge: 2.0, // Ring lives for 2 seconds
                delay: i * 0.5 // Stagger start times
            };
            
            group.add(ring);
        }
        
        return group;
    }

    startSignal(id, bodyColor = 0x00ff00, speed = 2.0) {
        const device = this.devices.get(id);
        if (!device) return;

        // Rings always RED regardless of body color
        const ringColor = 0xff0000;

        device.ringData.active = true;
        device.ringData.color.setHex(ringColor);
        device.ringData.speed = speed;
        device.ringData.lastSpawn = 0;

        // Set device body color via custom shader
        const state = bodyColor === 0xff0000 ? 'alarm' : 'test';
        this.updateDeviceMaterial(device.mesh, state);

        // Initialize rings (always red)
        device.rings.children.forEach((ring, i) => {
            ring.userData.age = -i * 0.5;
            ring.material.color.setHex(ringColor);
            ring.material.opacity = 0;
            ring.scale.set(1, 1, 1);
        });
    }

    stopSignal(id) {
        const device = this.devices.get(id);
        if (!device) return;

        device.ringData.active = false;

        // Fade out all rings immediately
        device.rings.children.forEach(ring => {
            ring.material.opacity = 0;
            ring.userData.age = 0;
        });
    }


    // Calculate distance between two slot positions
    getDistance(pos1, pos2) {
        return pos1.distanceTo(pos2);
    }

    // Get all slot positions from devices
    getAllDevicePositions() {
        const positions = [];
        this.devices.forEach((device, id) => {
            positions.push({
                id: id,
                position: device.mesh.position,
                device: device
            });
        });
        return positions;
    }

    // Fire simulation with proximity propagation
    simulateFireWithPropagation() {
        // Get origin device (random)
        const ids = Array.from(this.devices.keys());
        const originId = ids[Math.floor(Math.random() * ids.length)];
        const originDevice = this.devices.get(originId);
        const originPos = originDevice.mesh.position;

        console.log(`Fire origin: ${originId}`);

        // Calculate distances from origin to all other devices
        const allPositions = this.getAllDevicePositions();
        const sortedByDistance = allPositions
            .map(item => ({
                ...item,
                distance: this.getDistance(originPos, item.position)
            }))
            .sort((a, b) => a.distance - b.distance);

        // Trigger alarms with delays
        sortedByDistance.forEach((item, index) => {
            const delay = index * 2000; // 0s, 2s, 4s

            setTimeout(() => {
                this.triggerAlarm(item.id);
            }, delay);
        });

        // Update status
        setTimeout(() => {
            eventBus.emit('fire:status', { count: 3 });
        }, 4000);
    }

    // Fire simulation starting at specific slot (for scripted demos)
    simulateFireAtSlot(slotName) {
        // Find device at specific slot
        let originDevice = null;
        let originId = null;

        this.devices.forEach((device, id) => {
            if (device.slotId === slotName) {
                originDevice = device;
                originId = id;
            }
        });

        if (!originDevice) {
            console.error(`No device found at slot: ${slotName}`);
            console.log(`Available slots:`, Array.from(this.devices.values()).map(d => d.slotId));
            return;
        }

        console.log(`🔥 Fire origin at ${slotName} (${originId})`);

        // Calculate distances from origin to all other devices
        const originPos = originDevice.mesh.position;
        const allPositions = this.getAllDevicePositions();

        const sortedByDistance = allPositions
            .map(item => ({
                ...item,
                distance: this.getDistance(originPos, item.position)
            }))
            .sort((a, b) => a.distance - b.distance)
            .slice(0, this.fireConfig.maxPropagation);

        console.log(`📍 Found ${sortedByDistance.length} detectors within propagation range`);

        // Trigger alarms with smarter delays
        const totalDevices = sortedByDistance.length;
        
        sortedByDistance.forEach((item, index) => {
            let delay;
            
            if (this.fireConfig.useDistanceBasedDelay) {
                // Distance-based: closer = faster response
                const maxDist = sortedByDistance[totalDevices - 1]?.distance || 1;
                const normalizedDist = item.distance / maxDist;
                delay = normalizedDist * this.fireConfig.maxDelay;
            } else {
                // Index-based: sequential with shorter intervals
                delay = Math.min(index * this.fireConfig.baseDelay, this.fireConfig.maxDelay);
            }
            
            // Instant for origin device
            if (index === 0) delay = 0;

            setTimeout(() => {
                this.triggerAlarm(item.id);
                console.log(`🔔 Alarm ${index + 1}/${totalDevices}: ${item.id} (delay: ${delay.toFixed(0)}ms)`);
            }, delay);
        });

        // Update status after all triggered
        const maxDelay = this.fireConfig.useDistanceBasedDelay 
            ? this.fireConfig.maxDelay 
            : Math.min((totalDevices - 1) * this.fireConfig.baseDelay, this.fireConfig.maxDelay);
            
        setTimeout(() => {
            eventBus.emit('fire:status', { count: totalDevices });
        }, maxDelay + 500);
    }

    // Trigger single device alarm
    triggerAlarm(id) {
        const device = this.devices.get(id);
        if (!device || device.state === 'offline') return;

        // Set to alarm state
        device.state = 'alarm';

        // Red body
        this.updateDeviceMaterial(device.mesh, 'alarm');

        // Start red signal rings (if not muted)
        if (!device.muted) {
            this.startSignal(id, 0xff0000, 2.0);
        }

        // Initialize smoke climb properties (0% → 85% over 3 seconds)
        device.smokeClimbActive = true;
        device.targetSmoke = 85;
        device.smokeClimbSpeed = 28; // 85 / 3 seconds
        device.smoke = 0; // Start from 0

        // Update UI
        eventBus.emit('device:updated', {
            id: id,
            state: 'alarm',
            type: device.type,
            location: device.location,
            signal: device.signal,
            battery: device.battery,
            smoke: 85.00, // Fire detected!
            lastTest: device.lastTest,
            muted: device.muted
        });

        console.log(`ALARM triggered on: ${id}`);
    }

    // Reset all devices from fire
    resetFire() {
        let alarmCount = 0;

        this.devices.forEach((device, id) => {
            if (device.state === 'alarm') {
                alarmCount++;

                // Stop rings
                this.stopSignal(id);

                // Reset to online
                device.state = 'online';
                this.updateDeviceMaterial(device.mesh, 'online');

                // Update UI
                eventBus.emit('device:updated', {
                    id: id,
                    state: 'online',
                    type: device.type,
                    location: device.location,
                    signal: device.signal,
                    battery: device.battery,
                    smoke: 0.00,
                    lastTest: device.lastTest,
                    muted: device.muted
                });
            }
        });

        eventBus.emit('fire:status', { count: 0 });
        console.log(`Fire reset: ${alarmCount} devices cleared`);
    }
    
    testDevice(id) {
        const device = this.devices.get(id);
        if (!device || device.state === 'offline') return;

        // Set device state to test and body to green
        device.state = 'test';
        this.updateDeviceMaterial(device.mesh, 'test');

        // Only start signal rings if NOT muted
        if (!device.muted) {
            this.startSignal(id, 0x00ff00, 2.0); // Green body + red rings
            console.log(`Test started on: ${id} with rings`);
        } else {
            console.log(`Test started on: ${id} (silent - muted)`);
        }

        // Emit update to trigger UI (red button if not muted, or just test state)
        eventBus.emit('device:updated', {
            id: id,
            state: 'test',
            type: device.type,
            location: device.location,
            signal: device.signal,
            battery: device.battery,
            smoke: device.smoke,
            lastTest: device.lastTest,
            muted: device.muted  // UI will show muted icon
        });

        // Auto-reset after 3 seconds
        setTimeout(() => {
            this.stopSignal(id); // Safe to call even if rings never started

            // Reset to normal online state
            device.state = 'online';
            this.updateDeviceMaterial(device.mesh, 'online');

            eventBus.emit('device:updated', {
                id: id,
                state: 'online',
                type: device.type,
                location: device.location,
                signal: device.signal,
                battery: device.battery,
                smoke: 0.00,
                lastTest: 0,
                muted: device.muted  // Preserves mute state
            });

            console.log(`Test completed on: ${id}`);
        }, 3000);
    }
    
    // update(deltaTime) {
    //     // DEBUG: Log first device state once per second to avoid spam
    //     if (Math.random() < 0.02) { // ~once per second at 60fps
    //         const firstDevice = Array.from(this.devices.values())[0];
    //         if (firstDevice) {
    //             console.log('[DEBUG] Device state:', firstDevice.state, 
    //                       'smokeClimbActive:', firstDevice.smokeClimbActive,
    //                       'smoke:', firstDevice.smoke);
    //         }
    //     }
        
    //     this.devices.forEach((device, id) => {
    //         // Existing ring code...
    //         if (device.ringData.active) {
    //             device.rings.children.forEach(ring => {
    //                 // ... keep existing ring animation code ...
    //             });
    //         }

    //         // DEBUG: Check if we enter the smoke climb block
    //         if (device.smokeClimbActive) {
    //             console.log(`[SMOKE CLIMB] ${id}: current=${device.smoke}, target=${device.targetSmoke}, delta=${deltaTime}`);
                
    //             const climbAmount = device.smokeClimbSpeed * deltaTime;
    //             device.smoke = Math.min(device.smoke + climbAmount, device.targetSmoke);
                
    //             console.log(`[SMOKE UPDATE] ${id}: new smoke=${device.smoke}, emitting event...`);
                
    //             eventBus.emit('device:updated', {
    //                 id: id,
    //                 state: device.state,
    //                 type: device.type,
    //                 location: device.location,
    //                 signal: device.signal,
    //                 battery: device.battery,
    //                 smoke: Math.round(device.smoke * 10) / 10,
    //                 lastTest: device.lastTest,
    //                 muted: device.muted
    //             });
                
    //             if (device.smoke >= device.targetSmoke) {
    //                 device.smokeClimbActive = false;
    //             }
    //         }
    //     });
    // }

    update(deltaTime) {
        this.devices.forEach((device, id) => {
            // Animate shader time uniform for pulsing glow effect
            if (device.mesh.userData.customMaterial) {
                device.mesh.userData.customMaterial.uniforms.time.value += deltaTime;
            }

            // Update signal rings (if active)
            if (device.ringData.active) {
                device.rings.children.forEach(ring => {
                    ring.userData.age += deltaTime;

                    if (ring.userData.age < 0) {
                        ring.material.opacity = 0;
                        return;
                    }

                    const progress = ring.userData.age / ring.userData.maxAge;

                    if (progress >= 1) {
                        ring.userData.age = 0;
                        ring.scale.set(1, 1, 1);
                        ring.material.opacity = 0.8;
                    } else {
                        const scale = 1 + (progress * 3);
                        ring.scale.set(scale, scale, 1);
                        ring.material.opacity = 0.8 * (1 - progress);
                    }
                });
            }

            // Handle smoke climb during fire (NEW CODE ADDED HERE)
            if (device.smokeClimbActive && device.state === 'alarm') {
                const climbAmount = device.smokeClimbSpeed * deltaTime;
                device.smoke = Math.min(device.smoke + climbAmount, device.targetSmoke);
                
                // Emit update to refresh UI card (throttled to every 5% to avoid spam)
                const currentSmokeRounded = Math.round(device.smoke);
                if (currentSmokeRounded % 5 === 0 || device.smoke >= device.targetSmoke) {
                    eventBus.emit('device:updated', {
                        id: id,
                        state: device.state,
                        type: device.type,
                        location: device.location,
                        signal: device.signal,
                        battery: device.battery,
                        smoke: Math.round(device.smoke * 10) / 10,
                        lastTest: device.lastTest,
                        muted: device.muted
                    });
                }
                
                // Stop when target reached
                if (device.smoke >= device.targetSmoke) {
                    device.smokeClimbActive = false;
                }
            }
        });
    }

    updateDeviceMaterial(device, state) {
        const customMaterial = device.userData?.customMaterial;
        if (!customMaterial) return;

        const colors = {
            online: { base: new THREE.Color(0x00aa00), emissive: new THREE.Color(0x00ff00) },
            offline: { base: new THREE.Color(0x888888), emissive: new THREE.Color(0x444444) },
            alarm: { base: new THREE.Color(0xaa0000), emissive: new THREE.Color(0xff0000) },
            test: { base: new THREE.Color(0xb45309), emissive: new THREE.Color(0xf59e0b) }
        };

        const colorSet = colors[state] || colors.online;
        customMaterial.uniforms.baseColor.value.copy(colorSet.base);
        customMaterial.uniforms.emissiveColor.value.copy(colorSet.emissive);
        customMaterial.uniforms.emissiveIntensity.value = state === 'offline' ? 0.2 : 1.2;
    }

    // Populate from manifest + slot positions
    populate(manifest, slotPositions) {
        manifest.forEach((config) => {
            // Find matching slot position
            const slot = slotPositions.find(s => s.name === config.slotId);
            if (slot) {
                this.createDevice(config, slot.position);
            } else {
                console.warn('No position for slot:', config.slotId);
            }
        });
        eventBus.emit('devices:loaded', Array.from(this.devices.values()));
    }

    // Get device by ID (for interaction)
    getDevice(id) {
        return this.devices.get(id);
    }

    // Simulate fire (random device goes to alarm state)
    simulateFire() {
        const ids = Array.from(this.devices.keys());
        const randomId = ids[Math.floor(Math.random() * ids.length)];

        // Start alarm: Red body + Red rings
        this.startSignal(randomId, 0xff0000, 2.0);
        this.devices.get(randomId).state = 'alarm';

        console.log('Fire simulated at:', randomId);
        return randomId;
    }

    // Toggle device on/off
    toggleDevice(id) {
        const device = this.devices.get(id);
        if (!device) return;

        // Prevent power toggle during test or alarm states
        if (device.state === 'test' || device.state === 'alarm') {
            console.log(`Cannot toggle power: ${id} is in ${device.state} state`);
            return;
        }

        const newState = device.state === 'online' ? 'offline' : 'online';
        device.state = newState;

        // Stop any active signal when going offline
        if (newState === 'offline') {
            this.stopSignal(id);
        }

        // Update material
        this.updateDeviceMaterial(device.mesh, newState);

        eventBus.emit('device:updated', { 
            id: id, 
            state: newState,
            type: device.type,
            location: device.location,
            signal: device.signal,
            battery: device.battery,
            smoke: device.smoke,
            lastTest: device.lastTest,
            muted: device.muted
        });

        return newState;        
    }

    toggleMute(id) {
        const device = this.devices.get(id);
        if (!device || device.state === 'offline') return; // Can't mute when offline

        device.muted = !device.muted;
        
        // Emit update to refresh UI
        eventBus.emit('device:updated', {
            id: id,
            state: device.state,
            type: device.type,
            location: device.location,
            signal: device.signal,
            battery: device.battery,
            smoke: device.smoke,
            lastTest: device.lastTest,
            muted: device.muted
        });

        return device.muted;
    }
}